import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export interface SubscriptionWithAmountDue {
  enrollment_id: string;
  student_name: string;
  subscription_name: string;
  amount_due: number;
  amount_paid: number;
  outstanding_balance: number;
  billing_date: string;
  due_date: string | null;
  late_fees: number;
  status: 'active' | 'pending' | 'cancelled';
  billing_account_id: string | null;
  user_id: string;
  child_id: string | null;
  billing_schedule_id?: string | null;
  payment_record_id?: string | null;
}

export interface FamilyGroup {
  billing_account_id: string;
  family_lead_name: string;
  family_lead_last_name: string;
  subscriptions: SubscriptionWithAmountDue[];
  total_due: number;
  credits: number;
  current_due: number;
  subscription_count: number;
}

interface UseOutstandingPaymentsParams {
  companyId: string | null;
  enabled?: boolean;
  /** When true, include families with zero balance (all members). Default false = only those with balance due. */
  includeZeroBalance?: boolean;
}

export function useOutstandingPayments({
  companyId,
  enabled = true,
  includeZeroBalance = false,
}: UseOutstandingPaymentsParams) {
  return useQuery({
    queryKey: ['subscriptionsWithAmountDue', { companyId, includeZeroBalance }],
    enabled: !!companyId && enabled,
    queryFn: async (): Promise<FamilyGroup[]> => {
      if (!companyId) return [];

      // Get active enrollments with billing account info
      const { data: enrollments, error: enrollmentsError } = await supabase
        .from('subscription_enrollments')
        .select(
          `
          id,
          status,
          user_id,
          child_id,
          billing_account_id,
          company_subscriptions!subscription_enrollments_subscription_id_fkey (
            name,
            price
          ),
          user_profiles!subscription_enrollments_user_id_fkey (
            first_name,
            last_name
          ),
          child_profiles!subscription_enrollments_child_id_fkey (
            first_name,
            last_name
          ),
          billing_account:user_profiles!subscription_enrollments_billing_account_id_fkey (
            first_name,
            last_name
          )
        `,
        )
        .eq('company_id', companyId)
        .in('status', ['active', 'pending']);

      if (enrollmentsError) {
        console.error('[useOutstandingPayments] Error fetching enrollments:', enrollmentsError);
        return [];
      }

      // Get payment records for these enrollments
      const enrollmentIds = (enrollments || []).map((e: { id: string }) => e.id);
      const { data: paymentRecords } =
        enrollmentIds.length > 0
          ? await supabase
              .from('payment_records')
              .select('id, enrollment_id, amount, status, billing_date')
              .in('enrollment_id', enrollmentIds)
          : { data: [] };

      // Get billing schedules for due dates
      const { data: billingSchedules } =
        enrollmentIds.length > 0
          ? await supabase
              .from('billing_schedules')
              .select('id, enrollment_id, scheduled_billing_date, status, amount')
              .in('enrollment_id', enrollmentIds)
              .eq('status', 'pending')
          : { data: [] };

      // Get late fees for these enrollments (late_fees table - use type assertion for generated types)
      type LateFeeRow = { enrollment_id: string; amount?: number | null };
      const { data: lateFeesRaw } =
        enrollmentIds.length > 0
          ? await (supabase.from as (t: string) => ReturnType<typeof supabase.from>)('late_fees')
              .select('enrollment_id, amount, paid_at')
              .in('enrollment_id', enrollmentIds)
              .is('paid_at', null) // Only unpaid late fees
          : { data: [] };
      const lateFees = (lateFeesRaw || []) as unknown as LateFeeRow[];

      // Calculate amounts due for each enrollment
      const subscriptions: SubscriptionWithAmountDue[] = [];

      type EnrollmentRow = {
        id: string;
        user_id: string;
        billing_account_id?: string | null;
        child_id?: string | null;
        status?: string | null;
        company_subscriptions?: { price?: number | null; name?: string | null } | null;
        user_profiles?: { first_name?: string | null; last_name?: string | null } | null;
        child_profiles?: { first_name?: string | null; last_name?: string | null } | null;
      };

      type PaymentRecord = {
        id: string;
        enrollment_id: string;
        status: string;
        amount?: number | null;
        billing_date?: string | null;
      };
      type BillingSchedule = {
        id: string;
        enrollment_id: string;
        scheduled_billing_date?: string | null;
      };

      (enrollments || []).forEach((enrollment: EnrollmentRow) => {
        const price = Number(enrollment.company_subscriptions?.price || 0);
        const user = enrollment.user_profiles;
        const child = enrollment.child_profiles;

        const studentName = child
          ? `${child.first_name || ''} ${child.last_name || ''}`.trim()
          : user
            ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
            : 'Unknown';

        const allPayments = ((paymentRecords || []) as PaymentRecord[]).filter(
          (p) => p.enrollment_id === enrollment.id,
        );
        const payments = allPayments.filter((p) => p.status === 'succeeded');
        const duePayments = allPayments.filter((p) => p.status === 'pending' || p.status === 'failed');

        const schedule = (billingSchedules || []).find(
          (s: BillingSchedule) => s.enrollment_id === enrollment.id,
        ) as BillingSchedule | undefined;

        const unpaidLateFees = lateFees.filter((lf) => lf.enrollment_id === enrollment.id);
        const lateFeesTotal = unpaidLateFees.reduce(
          (sum: number, lf) => sum + Number(lf.amount || 0),
          0,
        );

        const amountPaid = payments.reduce((sum: number, p: PaymentRecord) => {
          const amt =
            typeof p.amount === 'number' ? p.amount : typeof p.amount === 'string' ? parseFloat(p.amount) : 0;
          return sum + (isNaN(amt) ? 0 : amt);
        }, 0);

        const amountDue = Number(price) + Number(lateFeesTotal);
        const outstandingBalance = Math.max(0, amountDue - amountPaid);

        const billingAccountId = enrollment.billing_account_id || enrollment.user_id;

        if (!includeZeroBalance && outstandingBalance === 0) return;

        if (includeZeroBalance || outstandingBalance > 0) {
          const duePaymentRecord =
            duePayments.length > 0
              ? duePayments.sort((a, b) => {
                  const dateA = a.billing_date ? new Date(a.billing_date).getTime() : 0;
                  const dateB = b.billing_date ? new Date(b.billing_date).getTime() : 0;
                  return dateB - dateA;
                })[0]
              : undefined;

          subscriptions.push({
            enrollment_id: enrollment.id,
            student_name: studentName,
            subscription_name: enrollment.company_subscriptions?.name || 'Unknown Subscription',
            amount_due: price,
            amount_paid: amountPaid,
            outstanding_balance: outstandingBalance,
            billing_date:
              payments.length > 0 && payments[0].billing_date
                ? payments[0].billing_date
                : schedule?.scheduled_billing_date || new Date().toISOString().split('T')[0],
            due_date: schedule?.scheduled_billing_date || null,
            late_fees: lateFeesTotal,
            status: (enrollment.status || 'active') as 'active' | 'pending' | 'cancelled',
            billing_account_id: billingAccountId,
            user_id: enrollment.user_id,
            child_id: enrollment.child_id || null,
            billing_schedule_id: schedule?.id || null,
            payment_record_id: duePaymentRecord?.id || null,
          });
        }
      });

      const familyGroupsMap = new Map<string, SubscriptionWithAmountDue[]>();

      subscriptions.forEach((sub) => {
        const billingId = sub.billing_account_id || sub.user_id;
        const group = familyGroupsMap.get(billingId);
        if (group) {
          group.push(sub);
        } else {
          familyGroupsMap.set(billingId, [sub]);
        }
      });

      const billingAccountIds = Array.from(familyGroupsMap.keys());
      const { data: billingAccountUsers } =
        billingAccountIds.length > 0
          ? await supabase
              .from('user_profiles')
              .select('id, first_name, last_name')
              .in('id', billingAccountIds)
          : { data: [] };

      const familyGroups: FamilyGroup[] = [];

      familyGroupsMap.forEach((subs, billingAccountId) => {
        const billingUser = (billingAccountUsers || []).find(
          (u: { id: string; first_name?: string | null; last_name?: string | null }) =>
            u.id === billingAccountId,
        );

        const totalDue = subs.reduce((sum, s) => sum + s.outstanding_balance, 0);
        const credits = 0;
        const currentDue = Math.max(0, totalDue - credits);

        familyGroups.push({
          billing_account_id: billingAccountId,
          family_lead_name: billingUser
            ? `${billingUser.first_name || ''} ${billingUser.last_name || ''}`.trim()
            : 'Unknown',
          family_lead_last_name: billingUser?.last_name || 'Unknown',
          subscriptions: subs,
          total_due: totalDue,
          credits,
          current_due: currentDue,
          subscription_count: subs.length,
        });
      });

      return familyGroups.sort((a, b) => b.current_due - a.current_due);
    },
  });
}
